import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import {
  injectAdTag,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { computed } from "vue";
import { injectDisplayEvents } from "../composition/displayEvents";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";

export function useShouldDisplayOffer() {
  const betData = injectBetData();
  const brand = injectBrand();
  const adTag = injectAdTag();
  const previewContext = injectPreview();

  const displayOffer = computed(() => {
    // Allow the preview to turn off the offer
    if (
      previewContext?.preview.value.isPreview &&
      previewContext?.preview.value.hideOffer
    ) {
      return false;
    }

    // Override for previewing the offer for an operator on brands page.
    if (
      previewContext?.preview.value.useBrandOffer &&
      betData?.operator.value &&
      brand?.offers.value?.[betData?.operator.value.name]
    ) {
      return true;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return true;
      // Check to see if we can use the brand based offer for the opertor
    } else {
      // Has an offer been defiend for this opertor
      if (
        adTag &&
        adTag.operator.value &&
        brand?.operatorHasOffer(adTag.operator.value.key)
      ) {
        // Are there no markets and should there a fallback offer be shown
        if (
          (!betData?.markets?.value ||
            Object.keys(betData.markets.value).length === 0) &&
          brand?.adTemplate.value.displayOffersAsFallback
        ) {
          return true;
        }

        // Are offers enabled for this brand
        if (brand?.adTemplate.value.displayOfferCard) {
          return true;
        }
      }
    }
    return false;
  });

  return displayOffer;
}

export function useOfferCardDisplayState() {
  /**
   * This checks to see if the offer was displayed as a fallback or not
   * used for the anayltics reporting
   */
  const offerDisplayedAsFallback = computed(() => {
    const brand = injectBrand();
    const adTag = injectAdTag();

    const displayEvents = injectDisplayEvents();
    // Has an offer been defiend for this opertor
    if (
      adTag &&
      adTag.operator.value &&
      brand?.operatorHasOffer(adTag.operator.value.key)
    ) {
      // Are there no markets and should there a fallback offer be shown
      if (
        (!displayEvents || displayEvents.orderedMarkets?.value?.length === 0) &&
        brand?.adTemplate.value.displayOffersAsFallback
      ) {
        return true;
      }
    }

    return false;
  });

  /**
   * This checks to see if the default offer was displayed.
   * used for the anayltics reporting
   */
  const defaultOfferDisplayedAsFirstCard = computed(() => {
    const betData = injectBetData();
    const brand = injectBrand();
    const adTag = injectAdTag();
    const displayEvents = injectDisplayEvents();
    // If there's no bets then the fallback offer will be used
    if (displayEvents?.orderedMarkets?.value?.length === 0) {
      return false;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return false;
    }

    // Has an offer been defiend for this opertor
    if (
      adTag &&
      adTag.operator.value &&
      brand?.operatorHasOffer(adTag.operator.value.key)
    ) {
      // Are offers enabled for this brand
      if (brand?.adTemplate.value.displayOfferCard) {
        return true;
      }
    }
    return false;
  });

  return {
    offerDisplayedAsFallback,
    defaultOfferDisplayedAsFirstCard
  };
}

export function useGetOffer() {
  const betData = injectBetData();
  const brand = injectBrand();
  const adTag = injectAdTag();
  const previewContext = injectPreview();

  const brandOffer = computed(() => {
    const operatorOffer =
      adTag &&
      brand &&
      adTag.operator.value &&
      brand.operatorHasOffer(adTag.operator.value.key) &&
      brand.offers.value?.[adTag.operator.value.key];

    return (
      operatorOffer &&
      generateLinks.offer(operatorOffer, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.betData.value?.clickUrl
      })
    );
  });

  const displayOffer = computed(() => {
    // Override for previewing the offer for an operator on brands page.
    if (
      adTag &&
      previewContext?.preview.value.useBrandOffer &&
      brand?.offers.value?.[adTag.operator.value.key]
    ) {
      return brandOffer.value;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return betData.offer.value;
      // Check to see if we can use the brand based offer for the opertor
    } else {
      // Has an offer been defiend for this opertor
      return brandOffer.value;
    }
  });

  return displayOffer;
}
